import React, { useState, useEffect } from 'react';
import { Package, Search, X } from 'lucide-react';

const CATEGORY_LABELS = {
  sheet: 'Sheet', tile: 'Tile', ridge: 'Ridge', valley: 'Valley',
  barge: 'Barge', spouting: 'Spouting', fastening: 'Fastening', flashing: 'Flashing',
  cleaning: 'Cleaning', recoat: 'Recoat', aquaseal: 'Dektite',
  safety: 'Safety', travel: 'Travel', urgent: 'Urgent', misc: 'Misc', underlay: 'Underlay',
};

export default function CatalogPicker({ api, appraisalId, propertyId, onAdd, onClose }) {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [roofTypes, setRoofTypes] = useState([]);
  const [relevantCats, setRelevantCats] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [selected, setSelected] = useState(null);
  const [qty, setQty] = useState(1);
  const [length, setLength] = useState(0);
  const [width, setWidth] = useState(0);
  const [wastage, setWastage] = useState(5);
  const [calcResult, setCalcResult] = useState(null);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (propertyId) {
      api.get(`/properties/${propertyId}/relevant-products?search=${search}&category=${category}`).then(r => {
        setProducts(r.data.products || []);
        setRoofTypes(r.data.roof_types || []);
        setRelevantCats(r.data.relevant_categories || []);
      }).catch(() => {
        api.get(`/products?page=1&limit=50&search=${search}&category=${category}`).then(r => setProducts(r.data.items));
      });
    } else {
      api.get(`/products?page=1&limit=50&search=${search}&category=${category}`).then(r => setProducts(r.data.items));
    }
  }, [api, propertyId, search, category]);

  useEffect(() => {
    api.get('/products/categories').then(r => setCategories(r.data));
  }, [api]);

  const selectProduct = async (p) => {
    setSelected(p);
    setQty(1);
    setCalcResult(null);
    // Auto-calculate if product has coverage factor
    if (p.cover_width_mm > 0 || p.unit_type !== 'each') {
      try {
        const { data } = await api.post('/products/calculate-quantity', {
          product_id: p._id, measured_length: length || 1, measured_width: width || 1, wastage_percent: wastage,
        });
        setCalcResult(data);
        setQty(data.quantity || 1);
      } catch {}
    }
  };

  const recalculate = async () => {
    if (!selected) return;
    try {
      const { data } = await api.post('/products/calculate-quantity', {
        product_id: selected._id, measured_length: length, measured_width: width, measured_area: length * width, wastage_percent: wastage,
      });
      setCalcResult(data);
      setQty(data.quantity || qty);
    } catch {}
  };

  const addToQuote = async () => {
    if (!selected) return;
    setAdding(true);
    try {
      await api.post('/products/to-quote-item', {
        product_id: selected._id, appraisal_id: appraisalId, quantity: qty, length: length,
      });
      onAdd();
    } catch (e) { console.error(e); }
    setAdding(false);
  };

  return (
    <div data-testid="catalog-picker" className="border-2 border-green-400 rounded-lg bg-green-50/30 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-bold text-green-800 flex items-center gap-2"><Package size={16} /> Add from Product Catalog</h4>
        <button data-testid="catalog-picker-close" onClick={onClose} className="text-slate-400 hover:text-red-500"><X size={16} /></button>
      </div>

      {roofTypes.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-700">
          Property roof types: <span className="font-bold">{roofTypes.join(', ')}</span> — showing {relevantCats.length} relevant material categories
        </div>
      )}

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="catalog-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search products..." className="w-full pl-8 pr-3 py-1.5 border border-[#E2E2DF] rounded text-sm bg-white" />
        </div>
        <select data-testid="catalog-category-filter" value={category} onChange={e => setCategory(e.target.value)} className="text-sm border border-[#E2E2DF] rounded px-2 py-1.5 bg-white">
          <option value="">All Relevant</option>
          {(relevantCats.length > 0 ? relevantCats : categories.map(c => c.category)).map(c => {
            const cat = typeof c === 'string' ? c : c.category;
            return <option key={cat} value={cat}>{(CATEGORY_LABELS[cat] || cat)}</option>;
          })}
        </select>
      </div>

      {!selected && (
        <div className="max-h-48 overflow-y-auto border border-[#E2E2DF] rounded bg-white">
          {products.map(p => (
            <button key={p._id} data-testid={`catalog-product-${p._id}`} onClick={() => selectProduct(p)} className="w-full text-left px-3 py-2 border-b border-[#E2E2DF] last:border-0 hover:bg-slate-50 transition-colors">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium">{p.name}</span>
                  {p.cover_width_mm > 0 && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">{p.cover_width_mm}mm cover</span>}
                  {p.unit_type && p.unit_type !== 'each' && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-600">per {p.unit_type}</span>}
                </div>
                <div className="text-xs text-slate-500">{p.supplier}</div>
              </div>
              <div className="text-xs text-slate-400 mt-0.5">Mat: ${p.materials_rate?.toFixed(2)} | Lab: ${p.labour_rate?.toFixed(2)} | Buy: ${p.buy_cost_rate?.toFixed(2)}</div>
            </button>
          ))}
          {products.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No products found</p>}
        </div>
      )}

      {selected && (
        <div className="border border-green-300 rounded-lg bg-white p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h5 className="text-sm font-bold text-slate-800">{selected.name}</h5>
              <p className="text-xs text-slate-500">{selected.supplier} | {CATEGORY_LABELS[selected.category] || selected.category}</p>
            </div>
            <button data-testid="catalog-change-product" onClick={() => { setSelected(null); setCalcResult(null); }} className="text-xs text-slate-400 hover:text-red-500">Change</button>
          </div>

          {selected.cover_width_mm > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs">
              <div className="font-bold text-amber-800">Coverage Auto-Adjusted</div>
              <div className="text-amber-700 mt-0.5">Cover width: {selected.cover_width_mm}mm | Factor: {selected.coverage_factor}x | <span className="font-bold">Need {((selected.coverage_factor - 1) * 100).toFixed(0)}% more</span> than measured area due to overlap</div>
            </div>
          )}

          <div className="grid grid-cols-4 gap-2">
            <div>
              <label className="text-xs text-slate-500">Length (m)</label>
              <input data-testid="catalog-length" type="number" step="0.1" value={length} onChange={e => setLength(parseFloat(e.target.value) || 0)} onBlur={recalculate} className="w-full px-2 py-1.5 border border-[#E2E2DF] rounded text-sm font-mono-num" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Width (m)</label>
              <input data-testid="catalog-width" type="number" step="0.1" value={width} onChange={e => setWidth(parseFloat(e.target.value) || 0)} onBlur={recalculate} className="w-full px-2 py-1.5 border border-[#E2E2DF] rounded text-sm font-mono-num" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Wastage %</label>
              <input data-testid="catalog-wastage" type="number" step="1" value={wastage} onChange={e => setWastage(parseFloat(e.target.value) || 0)} onBlur={recalculate} className="w-full px-2 py-1.5 border border-[#E2E2DF] rounded text-sm font-mono-num" />
            </div>
            <div>
              <label className="text-xs text-slate-500 font-bold">Quantity</label>
              <input data-testid="catalog-quantity" type="number" step="0.1" value={qty} onChange={e => setQty(parseFloat(e.target.value) || 0)} className="w-full px-2 py-1.5 border border-green-400 rounded text-sm font-mono-num bg-green-50 font-bold" />
            </div>
          </div>

          {calcResult && (
            <div className="bg-slate-50 rounded-lg p-3 grid grid-cols-4 gap-3 text-xs">
              <div><span className="text-slate-500">Materials:</span><div className="font-bold font-mono-num">${calcResult.materials_cost?.toFixed(2)}</div></div>
              <div><span className="text-slate-500">Labour:</span><div className="font-bold font-mono-num">${calcResult.labour_cost?.toFixed(2)}</div></div>
              <div><span className="text-slate-500">Buy Cost:</span><div className="font-bold font-mono-num">${calcResult.buy_cost?.toFixed(2)}</div></div>
              <div><span className="text-slate-500">Total:</span><div className="font-bold font-mono-num text-green-700">${calcResult.total_cost?.toFixed(2)}</div></div>
            </div>
          )}

          {!calcResult && (
            <div className="bg-slate-50 rounded-lg p-3 grid grid-cols-4 gap-3 text-xs">
              <div><span className="text-slate-500">Materials:</span><div className="font-bold font-mono-num">${(qty * (selected.materials_rate || 0)).toFixed(2)}</div></div>
              <div><span className="text-slate-500">Labour:</span><div className="font-bold font-mono-num">${(qty * (selected.labour_rate || 0)).toFixed(2)}</div></div>
              <div><span className="text-slate-500">Buy Cost:</span><div className="font-bold font-mono-num">${(qty * (selected.buy_cost_rate || 0)).toFixed(2)}</div></div>
              <div><span className="text-slate-500">Total:</span><div className="font-bold font-mono-num text-green-700">${(qty * ((selected.materials_rate || 0) + (selected.labour_rate || 0))).toFixed(2)}</div></div>
            </div>
          )}

          <button data-testid="catalog-add-to-quote" onClick={addToQuote} disabled={adding || qty <= 0} className="w-full px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-bold hover:bg-green-700 disabled:opacity-50 transition-colors">
            {adding ? 'Adding...' : `Add to Quote — $${(qty * ((selected.materials_rate || 0) + (selected.labour_rate || 0))).toFixed(2)}`}
          </button>
        </div>
      )}
    </div>
  );
}
